/**
 * Result Counts Hook — server-side count() aggregations for the dashboard cards.
 *
 * Replaces the JS-side count over `useResults(undefined, 10000)` that capped at
 * the most recent 10K result docs and would silently undercount any client past
 * that mark. Uses Firestore's count() aggregation so the numbers always match
 * the Google Sheet (which appends every result via flushSheetWrites).
 *
 * Cost: 6 reads per refresh (5 buckets + total), regardless of result-doc count.
 * Refresh: every 30 s while the tab is visible, and on visibilitychange→visible.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useEffectiveClientId } from './useImpersonation';

export interface ResultCounts {
  good: number;
  later: number;
  bad: number;
  notInterested: number;
  nonAccredited: number;
  pending: number;
  total: number;
}

const EMPTY_COUNTS: ResultCounts = {
  good: 0,
  later: 0,
  bad: 0,
  notInterested: 0,
  nonAccredited: 0,
  pending: 0,
  total: 0,
};

const REFRESH_MS = 30_000;

export function useResultCounts() {
  const effectiveClientId = useEffectiveClientId();
  const [counts, setCounts] = useState<ResultCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!effectiveClientId) {
      setCounts(EMPTY_COUNTS);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCounts = async () => {
      const resultsRef = collection(db, 'clients', effectiveClientId, 'results');
      try {
        // 5 bucket counts + 1 total count, fanned out in parallel.
        const [good, later, notInterested, bad, nonAccredited, total] =
          await Promise.all([
            getCountFromServer(query(resultsRef, where('classification', '==', 'good_leads'))),
            getCountFromServer(query(resultsRef, where('classification', '==', 'good_leads_later'))),
            getCountFromServer(query(resultsRef, where('classification', '==', 'not_interested'))),
            getCountFromServer(query(resultsRef, where('classification', '==', 'bad_leads'))),
            getCountFromServer(query(resultsRef, where('classification', '==', 'non_accredited'))),
            getCountFromServer(query(resultsRef)),
          ]);
        if (cancelled) return;
        setCounts({
          good: good.data().count,
          later: later.data().count,
          notInterested: notInterested.data().count,
          bad: bad.data().count,
          nonAccredited: nonAccredited.data().count,
          // `pending` here = "result doc with a classification we don't bucket"
          // (e.g. 'pending', 'dead_leads'). Kept on the shape for parity with
          // the previous useResults counts, but the dashboard doesn't surface it.
          pending: Math.max(
            0,
            total.data().count -
              (good.data().count +
                later.data().count +
                notInterested.data().count +
                bad.data().count +
                nonAccredited.data().count),
          ),
          total: total.data().count,
        });
      } catch (err) {
        // Read failures (offline, rules denial mid-deploy) shouldn't strand the
        // dashboard in a permanent loading state. Log and leave previous counts.
        console.error('useResultCounts: fetch failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchCounts();
    const interval = window.setInterval(fetchCounts, REFRESH_MS);

    // Refresh immediately when the user comes back to the tab — tab can sit
    // backgrounded for hours, and the first frame after switching back should
    // already show fresh numbers rather than waiting for the next interval tick.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void fetchCounts();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [effectiveClientId]);

  return { counts, loading };
}
